// groceryUtils.js - Common utility functions for both measurement systems

import { DEFAULT_UNIT_CONVERSIONS, PRODUCT_METADATA } from './groceryConstants';
import { usesMetricSystem } from './measurementSystemUtils';

/**
 * Normalize ingredient names for consistent matching
 * @param {String} name - Raw ingredient name
 * @returns {String} Normalized name
 */
export const normalizeIngredientName = (name) => {
  if (!name) return '';
  
  // First clean up the string
  let normalized = name.toLowerCase()
    .replace(/\([^)]*\)/g, '')            // Remove parenthesis and contents
    .replace(/[-./#!$%^&*;:{}=_`~()]/g, ' ')  // Replace punctuation with spaces
    .replace(/\s+/g, ' ')                 // Collapse multiple spaces
    .trim();
  
  // COMPOUND INGREDIENTS: Protect multi-word ingredients that should NOT be split
  // These are products where one word alone means something completely different
  const compoundIngredients = {
    'egg noodles': 'egg-noodles',
    'egg noodle': 'egg-noodles',
    'rice noodles': 'rice-noodles',
    'rice noodle': 'rice-noodles',
    'glass noodles': 'glass-noodles',
    'soba noodles': 'soba-noodles',
    'udon noodles': 'udon-noodles',
    'ramen noodles': 'ramen-noodles',
    'coconut milk': 'coconut-milk',
    'coconut cream': 'coconut-cream',
    'almond milk': 'almond-milk',
    'soy milk': 'soy-milk',
    'oat milk': 'oat-milk',
    'rice milk': 'rice-milk',
    'peanut butter': 'peanut-butter',
    'almond butter': 'almond-butter',
    'apple cider vinegar': 'apple-cider-vinegar',
    'rice vinegar': 'rice-vinegar',
    'balsamic vinegar': 'balsamic-vinegar',
    'fish sauce': 'fish-sauce',
    'soy sauce': 'soy-sauce',
    'oyster sauce': 'oyster-sauce',
    'hoisin sauce': 'hoisin-sauce',
    'tomato paste': 'tomato-paste',
    'tomato sauce': 'tomato-sauce',
    'tomato puree': 'tomato-puree',
    'cream cheese': 'cream-cheese',
    'cottage cheese': 'cottage-cheese',
    'ricotta cheese': 'ricotta-cheese',
    'goat cheese': 'goat-cheese',
    'feta cheese': 'feta-cheese',
    'blue cheese': 'blue-cheese',
    'cheddar cheese': 'cheddar-cheese',
    'mozzarella cheese': 'mozzarella-cheese',
    'parmesan cheese': 'parmesan-cheese',
    'swiss cheese': 'swiss-cheese',
    'pepper jack cheese': 'pepper-jack-cheese',
    'chicken breast': 'chicken-breast',
    'chicken thigh': 'chicken-thighs',
    'chicken thighs': 'chicken-thighs',
    'chicken wing': 'chicken-wings',
    'chicken wings': 'chicken-wings',
    'chicken drumstick': 'chicken-drumsticks',
    'chicken drumsticks': 'chicken-drumsticks',
    'pork loin': 'pork-loin',
    'pork belly': 'pork-belly',
    'pork ribs': 'pork-ribs',
    'pork chop': 'pork-chops',
    'pork chops': 'pork-chops',
    'beef sirloin': 'beef-sirloin',
    'beef tenderloin': 'beef-tenderloin',
    'ground beef': 'ground-beef',
    'minced beef': 'minced-beef',
    'stewing beef': 'stewing-beef',
    'lamb chop': 'lamb-chops',
    'lamb chops': 'lamb-chops',
    'bell pepper': 'bell-pepper',
    'red bell pepper': 'red-bell-pepper',
    'green bell pepper': 'green-bell-pepper',
    'yellow bell pepper': 'yellow-bell-pepper',
    'orange bell pepper': 'orange-bell-pepper',
    'sweet potato': 'sweet-potato',
    'sweet potatoes': 'sweet-potato',
    'green onion': 'green-onion',
    'green onions': 'green-onion',
    'spring onion': 'spring-onion',
    'spring onions': 'spring-onion',
    'red onion': 'red-onion',
    'white onion': 'white-onion',
    'yellow onion': 'yellow-onion',
    'sweet onion': 'sweet-onion',
    'sweet onions': 'sweet-onion',
    'vidalia onion': 'sweet-onion',  // Vidalia is a type of sweet onion
    'olive oil': 'olive-oil',
    'extra virgin olive oil': 'extra-virgin-olive-oil',
    'vegetable oil': 'vegetable-oil',
    'sesame oil': 'sesame-oil',
    'sunflower oil': 'sunflower-oil',
    'canola oil': 'canola-oil',
    'brown sugar': 'brown-sugar',
    'powdered sugar': 'powdered-sugar',
    'icing sugar': 'icing-sugar',
    'maple syrup': 'maple-syrup',
    'corn syrup': 'corn-syrup',
    'vanilla extract': 'vanilla-extract',
    'almond extract': 'almond-extract',
    'baking powder': 'baking-powder',
    'baking soda': 'baking-soda',
    'sea salt': 'sea-salt',
    'kosher salt': 'kosher-salt',
    'black pepper': 'black-pepper',
    'white pepper': 'white-pepper',
    'cayenne pepper': 'cayenne-pepper',
    'chili powder': 'chili-powder',
    'curry powder': 'curry-powder',
    'garlic powder': 'garlic-powder',
    'onion powder': 'onion-powder',
    'smoked paprika': 'smoked-paprika',
    'sweet paprika': 'sweet-paprika',
    'bay leaf': 'bay-leaf',
    'bay leaves': 'bay-leaf',
    'basil leaves': 'basil-leaves',
    'mint leaves': 'mint-leaves',
    'cilantro leaves': 'cilantro-leaves',
    'parsley leaves': 'parsley-leaves',
    'romaine lettuce': 'romaine-lettuce',
    'iceberg lettuce': 'iceberg-lettuce',
    'butter lettuce': 'butter-lettuce',
    'boston lettuce': 'boston-lettuce',
    'sea bass': 'sea-bass',
    'striped bass': 'striped-bass',
    'smoked salmon': 'smoked-salmon',
    'canned tuna': 'canned-tuna',
    'canned salmon': 'canned-salmon',
    'dried oregano': 'dried-oregano',
    'dried basil': 'dried-basil',
    'dried thyme': 'dried-thyme',
    'dried rosemary': 'dried-rosemary',
    'dried parsley': 'dried-parsley',
    'fresh dill': 'fresh-dill',
    'fresh basil': 'fresh-basil',
    'fresh parsley': 'fresh-parsley',
    'fresh cilantro': 'fresh-cilantro',
    'fresh mint': 'fresh-mint',
    'fresh thyme': 'fresh-thyme',
    'fresh rosemary': 'fresh-rosemary',
    'whole milk': 'whole-milk',
    'skim milk': 'skim-milk',
    'low fat milk': 'low-fat-milk',
    'heavy cream': 'heavy-cream',
    'whipping cream': 'whipping-cream',
    'sour cream': 'sour-cream',
    'cooking cream': 'cooking-cream',
    'greek yogurt': 'greek-yogurt',
    'plain yogurt': 'plain-yogurt',
    'plain flour': 'plain-flour',
    'all purpose flour': 'all-purpose-flour',
    'bread flour': 'bread-flour',
    'whole wheat flour': 'whole-wheat-flour',
    'almond flour': 'almond-flour',
    'coconut flour': 'coconut-flour',
    'white wine': 'white-wine',
    'red wine': 'red-wine',
    'white wine vinegar': 'white-wine-vinegar',
    'red wine vinegar': 'red-wine-vinegar',
    'vegetable broth': 'vegetable-broth',
    'chicken broth': 'chicken-broth',
    'beef broth': 'beef-broth',
    'vegetable stock': 'vegetable-stock',
    'chicken stock': 'chicken-stock',
    'beef stock': 'beef-stock',
    'basmati rice': 'basmati-rice',
    'jasmine rice': 'jasmine-rice',
    'brown rice': 'brown-rice',
    'white rice': 'white-rice',
    'wild rice': 'wild-rice',
    'arborio rice': 'arborio-rice',
    'long grain rice': 'long-grain-rice',
    'short grain rice': 'short-grain-rice',
    'whole wheat bread': 'whole-wheat-bread',
    'white bread': 'white-bread',
    'sourdough bread': 'sourdough-bread',
    'french bread': 'french-bread',
    'italian bread': 'italian-bread',
    'rye bread': 'rye-bread',
    'fruit jam': 'fruit-jam',
    'strawberry jam': 'strawberry-jam',
    'raspberry jam': 'raspberry-jam',
    'apricot jam': 'apricot-jam',
    'mixed fruit puree': 'mixed-fruit-puree',
    'fruit puree': 'fruit-puree',
    'apple sauce': 'apple-sauce',
    'frozen mushrooms': 'frozen-mushrooms',
    'frozen peas': 'frozen-peas',
    'frozen corn': 'frozen-corn',
    'frozen spinach': 'frozen-spinach',
    'frozen berries': 'frozen-berries',
    'mixed berries': 'mixed-berries',
    'fresh spinach': 'fresh-spinach',
    'baby spinach': 'baby-spinach',
    'turkey thigh chops': 'turkey-cutlets',
    'turkey breast': 'turkey-breast',
    'free range eggs': 'free-range-eggs',
    'free range egg': 'free-range-eggs',
    // Cooked/prepared ingredients (to prevent mixing with raw versions)
    'boiled eggs': 'boiled-eggs',
    'boiled egg': 'boiled-eggs',
    'hard boiled eggs': 'hard-boiled-eggs',
    'hard boiled egg': 'hard-boiled-eggs',
    'soft boiled eggs': 'soft-boiled-eggs',
    'soft boiled egg': 'soft-boiled-eggs',
    'boiled potatoes': 'boiled-potatoes',
    'boiled potato': 'boiled-potatoes',
    'mashed potatoes': 'mashed-potatoes',
    'baked potatoes': 'baked-potatoes',
    'baked potato': 'baked-potatoes',
    'boiled carrots': 'boiled-carrots',
    'boiled carrot': 'boiled-carrots',
    'roasted vegetables': 'roasted-vegetables',
    'mixed mushrooms': 'mixed-mushrooms',
    'mixed vegetables': 'mixed-vegetables',
    // Asian ingredients
    'bean sprouts': 'bean-sprouts',
    'bean sprout': 'bean-sprouts',
    'snap peas': 'snap-peas',
    'snow peas': 'snow-peas',
    'sugar snap peas': 'sugar-snap-peas',
    'napa cabbage': 'napa-cabbage',
    'bok choy': 'bok-choy',
    'pak choi': 'bok-choy',
    'sesame seeds': 'sesame-seeds',
    'toasted sesame oil': 'toasted-sesame-oil',
    'steamed rice': 'steamed-rice',
    'sticky rice': 'sticky-rice',
    'fried rice': 'fried-rice',
    'five spice': 'five-spice',
    'five spice powder': 'five-spice-powder',
    'star anise': 'star-anise',
    'szechuan pepper': 'szechuan-pepper',
    'chili oil': 'chili-oil',
    'chili flakes': 'chili-flakes',
    'rice wine': 'rice-wine',
    'shaoxing wine': 'shaoxing-wine',
    'mirin': 'mirin',
    'toast bread': 'toast-bread',
    'sliced bread': 'sliced-bread',
  };
  
  // Check and replace compound ingredients BEFORE other processing
  for (const [compound, replacement] of Object.entries(compoundIngredients)) {
    if (normalized.includes(compound)) {
      normalized = normalized.replace(compound, replacement);
    }
  }
  
  // Check if this is a milk product before removing descriptors
  const isMilkProduct = normalized.includes('milk') || normalized.includes('leche');
  
  // Remove common descriptors that interfere with matching
  const descriptors = [
    'medium', 'large', 'small', 'fresh', 'frozen', 'dried', 'canned',
    'sliced', 'diced', 'chopped', 'minced', 'grated',
    'boneless', 'skinless', 'lean', 'extra', 'virgin',
    'ripe'
  ];
  
  // For milk products, preserve important fat content descriptors
  if (!isMilkProduct) {
    descriptors.push('whole');
  }
  
  for (const descriptor of descriptors) {
    normalized = normalized.replace(new RegExp(`\\b${descriptor}\\b`, 'g'), ' ');
  }
  normalized = normalized.replace(/\s+/g, ' ').trim();
  
  // Handle special plural forms
  const plurals = {
    eggs: 'egg', 
    tomatoes: 'tomato', 
    potatoes: 'potato', 
    peppers: 'pepper', 
    onions: 'onion',
    scallions: 'scallion',
    leeks: 'leek',
    mushrooms: 'mushroom',
    prawns: 'prawn',
    noodles: 'noodle',
    carrots: 'carrot', 
    cloves: 'clove', 
    fillets: 'fillet', 
    pieces: 'piece', 
    slices: 'slice',
    leaves: 'leaf',
    sprigs: 'sprig',
    stalks: 'stalk'
  };
  const words = normalized.split(' ');
  const normalizedWords = words.map(word => plurals[word] || word);
  return normalizedWords.join(' ');
};

/**
 * Parse unit size string to extract numeric value
 * @param {String} unitString - Unit size string (e.g. "500 g", "1 kg", "12 pieces")
 * @returns {Number} Numeric value extracted from the unit string, or 1 if not found
 */
export const parseUnitSize = (unitString) => {
  if (!unitString) return 1;
  
  // Check for formats like "1 piece (200 g)"
  const parenthesesMatch = unitString.match(/\((\d+(\.\d+)?)\s*([a-zA-Z]+)\)/);
  if (parenthesesMatch) {
    return parseFloat(parenthesesMatch[1]);
  }
  
  // Check for formats like "500 g", "1 kg", etc.
  const matches = unitString.match(/(\d+(\.\d+)?)\s*([a-zA-Z]+)/);
  if (matches) {
    return parseFloat(matches[1]);
  }
  
  // If just a number
  const numberMatch = unitString.match(/^(\d+(\.\d+)?)$/);
  if (numberMatch) {
    return parseFloat(numberMatch[1]);
  }
  
  return 1; // Default value if parsing fails
};

/**
 * Determine the measurement system for a country
 * @param {String} country - Country name or code
 * @returns {String} 'metric' or 'imperial'
 */
export const determineMeasurementSystem = (country) => {
  return usesMetricSystem(country) ? 'metric' : 'imperial';
};

/**
 * Find product metadata based on ingredient name
 * @param {String} ingredientName - Name of the ingredient
 * @returns {Object|null} Product metadata or null if not found
 */
export const findProductMetadata = (ingredientName) => {
  if (!ingredientName) return null;
  
  const normalizedName = normalizeIngredientName(ingredientName);
  
  // Direct match to product ID
  if (PRODUCT_METADATA[normalizedName]) {
    return PRODUCT_METADATA[normalizedName];
  }
  
  // Search through all products and their synonyms
  for (const [id, metadata] of Object.entries(PRODUCT_METADATA)) {
    if (normalizeIngredientName(id) === normalizedName) {
      return metadata;
    }
    
    // Check synonyms
    const synonyms = metadata.synonyms || [];
    if (synonyms.some(syn => normalizeIngredientName(syn) === normalizedName)) {
      return metadata;
    }
  }
  
  // Enhanced fallback: try partial matching for AI-generated names
  // This helps with names like "Fresh Bell Pepper" -> "bell-peppers"
  const words = normalizedName.split(' ').filter(word => word.length > 2);
  
  for (const [id, metadata] of Object.entries(PRODUCT_METADATA)) {
    const idWords = normalizeIngredientName(id).split(' ');
    
    // Check if all significant words from the ingredient are in the product ID
    if (words.length > 0 && words.every(word => 
      idWords.some(idWord => idWord.includes(word) || word.includes(idWord))
    )) {
      return metadata;
    }
    
    // Check against synonyms with partial matching
    const synonyms = metadata.synonyms || [];
    for (const synonym of synonyms) {
      const synWords = normalizeIngredientName(synonym).split(' ');
      if (words.length > 0 && words.every(word => 
        synWords.some(synWord => synWord.includes(word) || word.includes(synWord))
      )) {
        return metadata;
      }
    }
  }
  
  return null;
};

/**
 * Get density for an ingredient from PRODUCT_METADATA
 * @param {String} ingredientName - Name of the ingredient
 * @returns {Number|null} Density in g/ml or null if not available
 */
export const getDensity = (ingredientName) => {
  const metadata = findProductMetadata(ingredientName);
  return metadata ? metadata.density : null;
};

/**
 * Get average weight for an ingredient based on measurement system
 * @param {String} ingredientName - Name of the ingredient
 * @param {String} system - 'metric' or 'imperial'
 * @returns {Number|null} Average weight in system units or null if not available
 */
export const getIngredientWeight = (ingredientName, system = 'metric') => {
  const metadata = findProductMetadata(ingredientName);
  if (!metadata || !metadata.avgWeight) return null;
  
  return metadata.avgWeight[system] || null;
};

/**
 * Create processor factory based on country's measurement system
 * @param {String} country - Country name or code
 * @returns {Function} The appropriate grocery processor function
 */
export const createGroceryProcessor = (country) => {
  const system = determineMeasurementSystem(country);
  
  if (system === 'imperial') {
    return require('./imperial/groceryProcessorImperial').processGroceryListImperial;
  } else {
    return require('./metric/groceryProcessorMetric').processGroceryListMetric;
  }
};

/**
 * Determine category for an ingredient
 * @param {String} ingredientName - Name of the ingredient
 * @returns {String} Category name (e.g., 'Produce', 'Dairy', etc.)
 */
export const getIngredientCategory = (ingredientName) => {
  const metadata = findProductMetadata(ingredientName);
  return metadata ? metadata.category : 'Other';
};
 
/**
 * Check if an ingredient is perishable
 * @param {String} ingredientName - Name of the ingredient
 * @returns {Boolean} True if perishable, false otherwise
 */
export const isPerishable = (ingredientName) => {
  const metadata = findProductMetadata(ingredientName);
  return metadata ? !!metadata.perishable : true; // Default to true for safety
};

/**
 * Get shelf life for an ingredient
 * @param {String} ingredientName - Name of the ingredient
 * @returns {Number} Shelf life in days (default: 7)
 */
export const getShelfLifeDays = (ingredientName) => {
  const metadata = findProductMetadata(ingredientName);
  return metadata && metadata.shelfLifeDays ? metadata.shelfLifeDays : 7; // Default: 1 week
};

/**
 * Check if two strings are related (for ingredient matching)
 * @param {String} str1 - First string
 * @param {String} str2 - Second string
 * @returns {Boolean} True if strings are likely related
 */
export const areRelatedStrings = (str1, str2) => {
  if (!str1 || !str2) return false;
  if (str1.includes(str2) || str2.includes(str1)) {
    const longer = str1.length >= str2.length ? str1 : str2;
    const shorter = str1.length < str2.length ? str1 : str2;
    if (shorter.length < 3) return false;
    if (shorter.length < longer.length * 0.5) return false;
    // If the shorter string matches as a whole word in the longer string, it's a strong match
    const wholeWordMatch = new RegExp(`\\b${shorter}\\b`).test(longer);
    if (wholeWordMatch) return true;
    // Otherwise, if the shorter comprises a large portion of the longer (80%+), consider it a match
    return shorter.length > longer.length * 0.8;
  }
  return false;
};